import { z } from 'zod';
import { TimestampSchema, UuidSchema } from './common';

// ----- Inbound (eventos que recebemos de terceiros) -----

export const WebhookSourceSchema = z.enum(['meta-cloud', 'bob-payments', 'meta-ads']);
export type WebhookSource = z.infer<typeof WebhookSourceSchema>;

export const WebhookEventStatusSchema = z.enum(['received', 'processed', 'failed', 'replayed']);
export type WebhookEventStatus = z.infer<typeof WebhookEventStatusSchema>;

export const WebhookEventSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  source: WebhookSourceSchema,
  eventType: z.string().min(1),
  status: WebhookEventStatusSchema,
  payloadJson: z.string(),
  errorMessage: z.string().optional(),
  receivedAt: TimestampSchema,
  processedAt: TimestampSchema.optional(),
});
export type WebhookEvent = z.infer<typeof WebhookEventSchema>;

export const WebhookEventsListQuerySchema = z.object({
  source: WebhookSourceSchema.optional(),
  status: WebhookEventStatusSchema.optional(),
  eventType: z.string().min(1).optional(),
  // coerce: query string chega como string — sem isto, ?page=2 dava HTTP 400.
  page: z.coerce.number().int().positive().optional(),
  limit: z.coerce.number().int().positive().max(100).optional(),
});
export type WebhookEventsListQuery = z.infer<typeof WebhookEventsListQuerySchema>;

export const WebhookEventsListResponseSchema = z.object({
  events: z.array(WebhookEventSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
});
export type WebhookEventsListResponse = z.infer<typeof WebhookEventsListResponseSchema>;

// ----- Outbound (subscriptions que o tenant configura) -----

export const WebhookOutboundEventTypeSchema = z.enum([
  'lead.created',
  'lead.opted_out',
  'message.delivered',
  'message.failed',
  'conversation.replied',
  'campaign.started',
  'campaign.completed',
  'cadence.run.started',
  'cadence.run.completed',
]);
export type WebhookOutboundEventType = z.infer<typeof WebhookOutboundEventTypeSchema>;

export const WebhookSubscriptionSchema = z.object({
  id: UuidSchema,
  tenantId: UuidSchema,
  url: z.string().url(),
  eventTypes: z.array(WebhookOutboundEventTypeSchema).min(1),
  secretMasked: z.string(),
  isActive: z.boolean(),
  createdAt: TimestampSchema,
  updatedAt: TimestampSchema,
});
export type WebhookSubscription = z.infer<typeof WebhookSubscriptionSchema>;

export const WebhookDeliveryStatusSchema = z.enum(['success', 'failed', 'pending']);
export type WebhookDeliveryStatus = z.infer<typeof WebhookDeliveryStatusSchema>;

export const WebhookDeliverySchema = z.object({
  id: UuidSchema,
  subscriptionId: UuidSchema,
  eventType: WebhookOutboundEventTypeSchema,
  status: WebhookDeliveryStatusSchema,
  responseStatus: z.number().int().optional(),
  latencyMs: z.number().int().nonnegative().optional(),
  errorMessage: z.string().optional(),
  attemptCount: z.number().int().min(1),
  deliveredAt: TimestampSchema,
});
export type WebhookDelivery = z.infer<typeof WebhookDeliverySchema>;

export const CreateWebhookSubscriptionInputSchema = z.object({
  url: z.string().url(),
  eventTypes: z.array(WebhookOutboundEventTypeSchema).min(1),
});
export type CreateWebhookSubscriptionInput = z.infer<typeof CreateWebhookSubscriptionInputSchema>;

export const UpdateWebhookSubscriptionInputSchema = z.object({
  url: z.string().url().optional(),
  eventTypes: z.array(WebhookOutboundEventTypeSchema).min(1).optional(),
  isActive: z.boolean().optional(),
});
export type UpdateWebhookSubscriptionInput = z.infer<typeof UpdateWebhookSubscriptionInputSchema>;

export const WebhookSubscriptionsListResponseSchema = z.object({
  subscriptions: z.array(WebhookSubscriptionSchema),
  total: z.number().int().nonnegative(),
});
export type WebhookSubscriptionsListResponse = z.infer<
  typeof WebhookSubscriptionsListResponseSchema
>;

export const WebhookDeliveriesListResponseSchema = z.object({
  deliveries: z.array(WebhookDeliverySchema),
  total: z.number().int().nonnegative(),
});
export type WebhookDeliveriesListResponse = z.infer<typeof WebhookDeliveriesListResponseSchema>;

export const RevealSecretResponseSchema = z.object({
  secret: z.string(),
});
export type RevealSecretResponse = z.infer<typeof RevealSecretResponseSchema>;
